import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityDefinition, Coding } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { getCodeableConceptCode } from '@bariendo/lib/services';
import { APPOINTMENT_TYPES_SYSTEM } from '@bariendo/lib/constants';
import { expandCodeSystem } from '@bariendo/lib/codesystem';
import { SchedulingPageContextValue } from '@/pages/SchedulingPage/SchedulingPageContext';
import { buildAppointmentTypeCodings } from '@/pages/SchedulingPage/utils/schedulingShared';

/**
 * Loads the appointment-type catalog (ActivityDefinitions + expanded CodeSystem) and exposes it as the
 * SchedulingPageContextValue. This lets us reuse OneTimeAvailabilityForm / AppointmentTypesInput outside the
 * Calendar page, which would otherwise be the only provider of this context.
 */
export function useAppointmentTypeContext(): SchedulingPageContextValue {
  const medplum = useMedplum();
  const [activityDefinitions, setActivityDefinitions] = useState<Map<string, ActivityDefinition>>(new Map());
  const [activityCodeSystem, setActivityCodeSystem] = useState<Coding[] | null>(null);

  const getActivityDefinitionForAppointmentType = useCallback(
    (code: string | undefined): ActivityDefinition | undefined => (code ? activityDefinitions.get(code) : undefined),
    [activityDefinitions]
  );

  useEffect(() => {
    let cancelled = false;
    async function fetchActivityDefinitions(): Promise<void> {
      try {
        const definitions = await medplum.searchResources('ActivityDefinition', { status: 'active', _count: '100' });
        const codeSystem = await expandCodeSystem(medplum, APPOINTMENT_TYPES_SYSTEM);
        if (cancelled) {
          return;
        }
        const definitionsMap = new Map<string, ActivityDefinition>();
        for (const def of definitions) {
          const code = getCodeableConceptCode(def.code);
          if (code) {
            definitionsMap.set(code, def);
          }
        }
        setActivityCodeSystem(codeSystem);
        setActivityDefinitions(definitionsMap);
      } catch (err) {
        console.error('Failed to fetch activity definitions:', err); // eslint-disable-line no-console
      }
    }
    fetchActivityDefinitions();
    return () => {
      cancelled = true;
    };
  }, [medplum]);

  const appointmentTypeCodings = useMemo(
    () => buildAppointmentTypeCodings(activityCodeSystem, getActivityDefinitionForAppointmentType),
    [activityCodeSystem, getActivityDefinitionForAppointmentType]
  );

  return useMemo(
    () => ({ appointmentTypeCodings, getActivityDefinitionForAppointmentType }),
    [appointmentTypeCodings, getActivityDefinitionForAppointmentType]
  );
}
